// src/configs/controlGuidance/reviewerControlGuidance.ts

export type ReviewerControlGuidance = {
  title: string;
  reviewer_guidance: string;
  evidence_to_review: string[];
  strong_indicators: string[];
  common_gaps: string[];
};

export const REVIEWER_CONTROL_GUIDANCE: Record<string, ReviewerControlGuidance> = {
  "GV.RM-01": {
    title: "Cybersecurity Risk Management",
    reviewer_guidance:
      "Verify that the organization has a defined cybersecurity risk management framework aligned with enterprise risk management practices.",
    evidence_to_review: [
      "Cybersecurity risk management policy",
      "Enterprise risk register",
      "Board or executive risk reports",
      "Risk assessment documentation",
    ],
    strong_indicators: [
      "Formal risk management methodology defined",
      "Risks prioritized based on likelihood and impact",
      "Executive reporting of cyber risks",
    ],
    common_gaps: [
      "Informal or undocumented risk management",
      "No risk register maintained",
      "Cyber risk not integrated with enterprise risk",
    ],
  },

  "GV.PO-01": {
    title: "Cybersecurity Policy",
    reviewer_guidance:
      "Confirm the organization maintains a formal cybersecurity policy framework approved by leadership.",
    evidence_to_review: [
      "Cybersecurity policy documents",
      "Governance committee records",
      "Policy approval records",
      "Policy review schedule",
    ],
    strong_indicators: [
      "Policies approved by senior leadership",
      "Policies reviewed annually",
      "Clear policy ownership defined",
    ],
    common_gaps: [
      "Outdated policies",
      "Policies not formally approved",
      "No review cycle defined",
    ],
  },

  "PR.AA-01": {
    title: "Identity & Access Management",
    reviewer_guidance:
      "Assess whether identity management and authentication controls are centrally managed and consistently enforced.",
    evidence_to_review: [
      "IAM policy",
      "Active Directory or IAM configuration",
      "MFA configuration",
      "Access review reports",
    ],
    strong_indicators: [
      "MFA enforced for privileged users",
      "Automated provisioning/deprovisioning",
      "Periodic access reviews",
    ],
    common_gaps: [
      "Shared privileged accounts",
      "No MFA enforcement",
      "Manual account management",
    ],
  },

  "PR.IR-01": {
    title: "Information Protection",
    reviewer_guidance:
      "Determine whether sensitive information and systems are protected through security controls and monitoring.",
    evidence_to_review: [
      "Data classification policy",
      "Encryption configuration",
      "Network segmentation diagrams",
      "Security monitoring tools",
    ],
    strong_indicators: [
      "Data classification implemented",
      "Encryption for sensitive data",
      "Segmented network architecture",
    ],
    common_gaps: [
      "No formal data classification",
      "Flat network architecture",
      "Weak protection of critical assets",
    ],
  },

  "DE.CM-01": {
    title: "Continuous Monitoring",
    reviewer_guidance:
      "Confirm that the organization continuously monitors networks and systems to detect anomalies or security events.",
    evidence_to_review: [
      "SIEM configuration",
      "Log collection architecture",
      "Security monitoring dashboards",
      "Detection rule documentation",
    ],
    strong_indicators: [
      "Centralized log collection",
      "Automated alerting",
      "Defined detection rules",
    ],
    common_gaps: [
      "Logs stored locally only",
      "Limited monitoring coverage",
      "No detection rule management",
    ],
  },

  "RS.MA-01": {
    title: "Incident Response Management",
    reviewer_guidance:
      "Verify that the organization maintains and executes a formal incident response process.",
    evidence_to_review: [
      "Incident response plan",
      "Incident runbooks",
      "Incident tickets",
      "Tabletop exercise documentation",
    ],
    strong_indicators: [
      "Incident response playbooks defined",
      "Response team roles documented",
      "Regular incident exercises",
    ],
    common_gaps: [
      "No defined response procedures",
      "No testing of incident plans",
      "Delayed incident reporting",
    ],
  },

  "RC.RP-01": {
    title: "Recovery Planning",
    reviewer_guidance:
      "Assess whether the organization can restore systems and operations following a cybersecurity incident.",
    evidence_to_review: [
      "Disaster recovery plans",
      "Backup architecture documentation",
      "Recovery testing reports",
      "Business continuity plans",
    ],
    strong_indicators: [
      "Regular recovery testing",
      "Defined recovery time objectives",
      "Secure backup storage",
    ],
    common_gaps: [
      "Backups not tested",
      "Recovery procedures undocumented",
      "Recovery times unknown",
    ],
  },

  "GV.OC-03": {
    title: "Organizational Cybersecurity Context",
    reviewer_guidance:
      "Verify that the organization understands its cybersecurity obligations, including regulatory, legal, and operational risk requirements.",
    evidence_to_review: [
      "Regulatory compliance documentation",
      "Internal cybersecurity governance framework",
      "Board risk oversight documentation",
      "Compliance reporting procedures",
    ],
    strong_indicators: [
      "Clear mapping of regulatory obligations to security controls",
      "Executive visibility of compliance posture",
      "Defined accountability for regulatory risk",
    ],
    common_gaps: [
      "No central compliance tracking",
      "Cybersecurity responsibilities unclear",
      "Regulatory requirements not integrated into security program",
    ],
  },

  "GV.SC-04": {
    title: "Supply Chain Risk Management",
    reviewer_guidance:
      "Determine whether the organization assesses and manages cybersecurity risks posed by vendors and third-party partners.",
    evidence_to_review: [
      "Vendor risk management policy",
      "Third-party security assessments",
      "Vendor security questionnaires",
      "Contractual cybersecurity clauses",
    ],
    strong_indicators: [
      "Formal third-party risk assessment program",
      "Security requirements embedded in contracts",
      "Periodic vendor reassessments",
    ],
    common_gaps: [
      "Vendors onboarded without security review",
      "No security clauses in contracts",
      "No monitoring of vendor security posture",
    ],
  },

  "GV.RR-02": {
    title: "Roles and Responsibilities",
    reviewer_guidance:
      "Confirm that cybersecurity roles and responsibilities are clearly defined and assigned across the organization.",
    evidence_to_review: [
      "Organizational charts",
      "Security governance documentation",
      "Job descriptions for security roles",
      "Responsibility assignment matrices",
    ],
    strong_indicators: [
      "Clearly defined CISO or security leadership role",
      "Documented accountability for security functions",
      "Separation of duties implemented",
    ],
    common_gaps: [
      "Security responsibilities unclear",
      "Security managed informally by IT staff",
      "No accountability for security decisions",
    ],
  },

  "ID.AM-01": {
    title: "Hardware Asset Inventory",
    reviewer_guidance:
      "Assess whether the organization maintains an accurate inventory of physical devices connected to the network.",
    evidence_to_review: [
      "Asset inventory database",
      "Endpoint management system records",
      "Device discovery reports",
      "Configuration management databases",
    ],
    strong_indicators: [
      "Automated asset discovery tools",
      "Regular inventory reconciliation",
      "Ownership assigned for all assets",
    ],
    common_gaps: [
      "Manual or outdated asset inventories",
      "Unknown devices on the network",
      "No ownership tracking",
    ],
  },

  "ID.AM-02": {
    title: "Software Asset Inventory",
    reviewer_guidance:
      "Confirm the organization tracks installed software across systems to reduce exposure to vulnerable applications.",
    evidence_to_review: [
      "Software inventory reports",
      "Endpoint management platform",
      "License management systems",
      "Patch management tools",
    ],
    strong_indicators: [
      "Automated software discovery",
      "Approved software baseline defined",
      "Unauthorized software flagged automatically",
    ],
    common_gaps: [
      "No centralized software inventory",
      "Unauthorized applications installed",
      "No software lifecycle management",
    ],
  },

  "ID.AM-03": {
    title: "Data Flow Mapping",
    reviewer_guidance:
      "Verify the organization understands how sensitive data moves between systems and across network boundaries.",
    evidence_to_review: [
      "Data flow diagrams",
      "System architecture documentation",
      "Network topology diagrams",
      "Data classification documentation",
    ],
    strong_indicators: [
      "Documented system and data architecture",
      "Data flows reviewed during system changes",
      "Data protection controls aligned with classification",
    ],
    common_gaps: [
      "Data flows undocumented",
      "Sensitive data flows across uncontrolled systems",
      "Incomplete architecture documentation",
    ],
  },

  "ID.RA-01": {
    title: "Vulnerability Identification",
    reviewer_guidance:
      "Determine whether the organization systematically identifies vulnerabilities in systems and applications.",
    evidence_to_review: [
      "Vulnerability scanning reports",
      "Penetration testing reports",
      "Vulnerability management platform dashboards",
      "Risk remediation tracking",
    ],
    strong_indicators: [
      "Regular automated vulnerability scanning",
      "Prioritized remediation based on severity",
      "Tracking of remediation progress",
    ],
    common_gaps: [
      "Scans performed irregularly",
      "Vulnerabilities remain unresolved",
      "No remediation tracking",
    ],
  },

  "ID.RA-02": {
    title: "Threat Intelligence",
    reviewer_guidance:
      "Verify the organization gathers and analyzes threat intelligence to anticipate potential cyber threats.",
    evidence_to_review: [
      "Threat intelligence subscriptions",
      "Security monitoring dashboards",
      "Threat briefings or reports",
      "Integration of intelligence into detection tools",
    ],
    strong_indicators: [
      "Threat intelligence integrated with security monitoring",
      "Analysts review threat information regularly",
      "Intelligence informs security controls",
    ],
    common_gaps: [
      "No external threat intelligence sources",
      "Intelligence not integrated into operations",
      "Limited awareness of emerging threats",
    ],
  },

  "PR.AT-01": {
    title: "Security Awareness Training",
    reviewer_guidance:
      "Assess whether employees receive cybersecurity awareness training appropriate to their roles.",
    evidence_to_review: [
      "Training curriculum",
      "Security awareness completion reports",
      "Phishing simulation results",
      "Training policy documentation",
    ],
    strong_indicators: [
      "Mandatory annual training",
      "Role-based training for high-risk roles",
      "Phishing simulation program",
    ],
    common_gaps: [
      "Training delivered inconsistently",
      "No tracking of completion",
      "Limited role-specific training",
    ],
  },

  "PR.AT-02": {
    title: "Role-Based Security Training",
    reviewer_guidance:
      "Verify that personnel with elevated privileges or cybersecurity responsibilities receive specialized training.",
    evidence_to_review: [
      "Administrator training documentation",
      "Security team certifications",
      "Privileged user training programs",
    ],
    strong_indicators: [
      "Specialized training for administrators",
      "Security staff maintain professional certifications",
      "Training updated for new threats",
    ],
    common_gaps: [
      "Privileged users receive no specialized training",
      "Training outdated or informal",
      "No verification of competency",
    ],
  },

  "PR.PS-01": {
    title: "Secure Configuration Management",
    reviewer_guidance:
      "Determine whether systems are configured securely and changes are controlled through formal processes.",
    evidence_to_review: [
      "Secure configuration baselines",
      "Change management documentation",
      "System hardening standards",
      "Configuration monitoring tools",
    ],
    strong_indicators: [
      "Baseline configurations defined",
      "Changes approved through formal processes",
      "Automated configuration compliance monitoring",
    ],
    common_gaps: [
      "No configuration standards",
      "Unauthorized configuration changes",
      "Limited change tracking",
    ],
  },

  "PR.PS-02": {
    title: "Secure Configuration & System Hardening",
    reviewer_guidance:
      "Assess whether secure configuration baselines and system hardening standards are applied across critical telecom infrastructure components.",
    evidence_to_review: [
      "System hardening standards and baselines (CIS, DISA STIGs)",
      "Configuration compliance audit reports",
      "Default credential removal evidence",
      "Automated configuration drift detection logs",
    ],
    strong_indicators: [
      "Hardening baselines defined per system class",
      "Automated compliance monitoring in place",
      "Default credentials and unnecessary services removed",
    ],
    common_gaps: [
      "No formal hardening standards",
      "Manual configuration reviews only",
      "Inconsistent hardening across environments",
    ],
  },

  "PR.PS-04": {
    title: "Log Records & Continuous Monitoring",
    reviewer_guidance:
      "Verify that log records are generated from critical systems and infrastructure and are made available for continuous monitoring and security analysis.",
    evidence_to_review: [
      "Log management architecture documentation",
      "Log retention and integrity policies",
      "SIEM or log aggregation platform configuration",
      "Continuous monitoring procedures",
    ],
    strong_indicators: [
      "Centralized log collection from critical systems",
      "Log integrity protections in place",
      "Logs feeding automated alerting and monitoring",
    ],
    common_gaps: [
      "Incomplete log coverage across infrastructure",
      "No log integrity or retention controls",
      "Logs not integrated with monitoring tools",
    ],
  },

  "DE.AE-06": {
    title: "Security Event Analysis",
    reviewer_guidance:
      "Assess whether detected events are analyzed to determine whether they represent security incidents.",
    evidence_to_review: [
      "Security monitoring alerts",
      "Incident triage procedures",
      "SOC analyst reports",
      "Detection rule documentation",
    ],
    strong_indicators: [
      "Security events triaged quickly",
      "Analysts investigate alerts systematically",
      "Event correlation implemented",
    ],
    common_gaps: [
      "Alerts ignored or unresolved",
      "No structured triage process",
      "Monitoring tools poorly configured",
    ],
  },

  "RS.CO-01": {
    title: "Incident Communication",
    reviewer_guidance:
      "Confirm the organization communicates cybersecurity incidents effectively to internal and external stakeholders.",
    evidence_to_review: [
      "Incident communication procedures",
      "Regulatory reporting procedures",
      "Incident notification records",
      "Crisis communication plans",
    ],
    strong_indicators: [
      "Defined communication protocols",
      "Stakeholders notified promptly",
      "Regulatory reporting procedures documented",
    ],
    common_gaps: [
      "Incident communication ad hoc",
      "No defined escalation process",
      "Delayed regulatory reporting",
    ],
  },

  "RS.AN-03": {
    title: "Incident Analysis",
    reviewer_guidance:
      "Determine whether incidents are analyzed to understand root causes and improve defenses.",
    evidence_to_review: [
      "Incident investigation reports",
      "Root cause analysis documentation",
      "Post-incident review records",
    ],
    strong_indicators: [
      "Formal incident investigations conducted",
      "Lessons learned documented",
      "Findings incorporated into security improvements",
    ],
    common_gaps: [
      "Incidents resolved without analysis",
      "Root causes not investigated",
      "No lessons learned process",
    ],
  },

  "RS.MI-01": {
    title: "Incident Containment",
    reviewer_guidance:
      "Verify that the organization can contain security incidents quickly to limit damage.",
    evidence_to_review: [
      "Incident response playbooks",
      "Incident response tickets",
      "System isolation procedures",
      "Containment documentation",
    ],
    strong_indicators: [
      "Incident containment procedures defined",
      "Automated containment capabilities",
      "Incident response team trained",
    ],
    common_gaps: [
      "Slow response to incidents",
      "No defined containment procedures",
      "Limited response coordination",
    ],
  },

  "GV.OV-01": {
    title: "Cybersecurity Governance Oversight",
    reviewer_guidance:
      "Verify that executive leadership and governance bodies actively oversee cybersecurity strategy and risk management.",
    evidence_to_review: [
      "Board or executive cybersecurity briefings",
      "Cybersecurity governance committee documentation",
      "Risk oversight reports",
      "Executive meeting minutes referencing cybersecurity",
    ],
    strong_indicators: [
      "Regular cybersecurity reporting to executive leadership",
      "Security governance committees established",
      "Cyber risk integrated into enterprise risk discussions",
    ],
    common_gaps: [
      "Limited executive visibility into cybersecurity risks",
      "Security decisions handled only at the IT level",
      "No governance oversight of cybersecurity programs",
    ],
  },

  "GV.SC-07": {
    title: "Supply Chain Security Monitoring",
    reviewer_guidance:
      "Assess whether the organization monitors third-party vendors for cybersecurity risks after onboarding.",
    evidence_to_review: [
      "Vendor monitoring reports",
      "Third-party risk reassessment documentation",
      "Vendor security rating tools or monitoring platforms",
      "Vendor incident notification procedures",
    ],
    strong_indicators: [
      "Continuous monitoring of vendor risk posture",
      "Periodic reassessments of critical vendors",
      "Defined vendor incident notification requirements",
    ],
    common_gaps: [
      "Vendor security only assessed during onboarding",
      "No monitoring of vendor risk posture",
      "Vendor security incidents not tracked",
    ],
  },

  "GV.ME-01": {
    title: "Cybersecurity Performance Metrics",
    reviewer_guidance:
      "Confirm that the organization measures and reports cybersecurity program performance using defined metrics.",
    evidence_to_review: [
      "Security KPI dashboards",
      "Cybersecurity program maturity assessments",
      "Executive security reports",
      "Security performance scorecards",
    ],
    strong_indicators: [
      "Security metrics tracked over time",
      "Executive reporting of cybersecurity metrics",
      "Metrics tied to risk reduction objectives",
    ],
    common_gaps: [
      "No measurable cybersecurity performance indicators",
      "Metrics collected but not used for decision making",
      "Security performance not reported to leadership",
    ],
  },

  "ID.RA-05": {
    title: "Threat and Vulnerability Risk Assessment",
    reviewer_guidance:
      "Verify that the organization evaluates the potential impact of identified threats and vulnerabilities.",
    evidence_to_review: [
      "Risk assessment documentation",
      "Vulnerability prioritization reports",
      "Risk scoring methodology",
      "Threat modeling documentation",
    ],
    strong_indicators: [
      "Formal risk scoring methodology",
      "Vulnerabilities prioritized based on business impact",
      "Threat modeling integrated into security planning",
    ],
    common_gaps: [
      "Vulnerabilities tracked but not risk-ranked",
      "No structured threat analysis",
      "Impact assessments not performed",
    ],
  },

  "PR.AA-05": {
    title: "Privileged Access Management",
    reviewer_guidance:
      "Assess whether privileged accounts are tightly controlled and monitored.",
    evidence_to_review: [
      "Privileged access management platform configuration",
      "Privileged account inventory",
      "Privileged access logs",
      "Access review documentation",
    ],
    strong_indicators: [
      "Privileged access managed through PAM solutions",
      "Session monitoring for administrative access",
      "Regular privileged access reviews",
    ],
    common_gaps: [
      "Shared administrator accounts",
      "Privileged accounts unmanaged or undocumented",
      "No monitoring of privileged activity",
    ],
  },

  "DE.CM-03": {
    title: "Security Log Monitoring Coverage",
    reviewer_guidance:
      "Verify that security monitoring includes logs from critical systems and infrastructure.",
    evidence_to_review: [
      "Log ingestion configuration",
      "SIEM source inventory",
      "Monitoring architecture diagrams",
      "Detection rule documentation",
    ],
    strong_indicators: [
      "Logs centralized across infrastructure and applications",
      "Monitoring coverage includes critical assets",
      "Alerts generated from correlated events",
    ],
    common_gaps: [
      "Logs collected but not monitored",
      "Key systems not integrated into monitoring tools",
      "No visibility into security events",
    ],
  },

  "DE.AE-02": {
    title: "Security Event Detection",
    reviewer_guidance:
      "Assess whether detection systems are tuned to identify meaningful security events while minimizing false positives.",
    evidence_to_review: [
      "Detection rule configuration",
      "SOC playbooks",
      "Alert triage procedures",
      "Detection engineering documentation",
    ],
    strong_indicators: [
      "Detection rules tuned based on threat intelligence",
      "Regular review of alert quality",
      "SOC analysts actively triage events",
    ],
    common_gaps: [
      "High alert noise",
      "Detection rules outdated",
      "Analysts overwhelmed with alerts",
    ],
  },

  "RS.CO-03": {
    title: "External Incident Communication",
    reviewer_guidance:
      "Verify the organization communicates incidents appropriately with regulators, partners, and affected stakeholders.",
    evidence_to_review: [
      "Regulatory incident reporting procedures",
      "Public communication plans",
      "External notification records",
      "Crisis communication playbooks",
    ],
    strong_indicators: [
      "Regulatory reporting timelines defined",
      "Clear escalation procedures for major incidents",
      "Legal and communications teams integrated into response",
    ],
    common_gaps: [
      "No regulatory notification procedures",
      "Delayed external reporting",
      "Incident communication handled informally",
    ],
  },

  "RS.MI-02": {
    title: "Incident Eradication",
    reviewer_guidance:
      "Confirm that the organization can eliminate threats from compromised systems following containment.",
    evidence_to_review: [
      "Incident remediation procedures",
      "Malware removal documentation",
      "System restoration procedures",
      "Forensic investigation reports",
    ],
    strong_indicators: [
      "Root cause of compromise identified",
      "Systems rebuilt or sanitized",
      "Remediation steps documented",
    ],
    common_gaps: [
      "Threats removed temporarily but not eradicated",
      "Systems returned to production without full remediation",
      "No documentation of eradication steps",
    ],
  },

  "RC.CO-01": {
    title: "Post-Incident Improvement",
    reviewer_guidance:
      "Verify the organization improves security controls based on lessons learned from incidents.",
    evidence_to_review: [
      "Post-incident review reports",
      "Lessons learned documentation",
      "Security improvement plans",
      "Updated policies or procedures",
    ],
    strong_indicators: [
      "Formal after-action reviews conducted",
      "Security controls updated after incidents",
      "Improvements tracked through remediation plans",
    ],
    common_gaps: [
      "Incidents closed without review",
      "Lessons learned not documented",
      "No changes made following incidents",
    ],
  },

  "PR.DS-11": {
    title: "Backup Protection, Integrity, and Recovery Data Security",
    reviewer_guidance:
      "Assess whether backup data, recovery assets, and restoration resources are protected against unauthorized access, corruption, alteration, deletion, and ransomware compromise.",
    evidence_to_review: [
      "Backup architecture diagrams",
      "Backup encryption configurations",
      "Immutable backup configurations",
      "Backup access control policies",
      "Backup retention schedules",
      "Recovery testing reports",
      "Restore validation evidence",
      "Backup monitoring and alerting logs",
    ],
    strong_indicators: [
      "Backups encrypted at rest and in transit",
      "Immutable or offline backup capabilities implemented",
      "Access to backup systems restricted and monitored",
      "Backup integrity validated regularly",
      "Recovery testing performed on a defined schedule",
      "Recovery objectives (RTO/RPO) validated through testing",
    ],
    common_gaps: [
      "Backups stored without encryption",
      "No immutable or offline backup protections",
      "Excessive privileged access to backup repositories",
      "Recovery testing not performed or undocumented",
      "Backup integrity validation not conducted",
      "Recovery capabilities assumed but not verified",
    ],
  },

  "ID.RA-06": {
    title: "Vulnerability Assessment",
    reviewer_guidance:
      "Determine whether the organization conducts recurring vulnerability assessments to evaluate security weaknesses, validate exposure, and inform remediation priorities.",
    evidence_to_review: [
      "Vulnerability assessment reports",
      "Penetration testing reports",
      "External attack surface review reports",
      "Vulnerability risk-rating methodology",
      "Remediation prioritization records",
      "Reassessment or validation testing results",
    ],
    strong_indicators: [
      "Vulnerability assessments are performed on a recurring schedule",
      "Internal and external assets are assessed based on risk and criticality",
      "Findings are prioritized using severity, exploitability, and business impact",
      "Remediation progress is tracked and reassessed",
      "Assessment results inform risk decisions and remediation planning",
    ],
    common_gaps: [
      "Vulnerability assessments are ad hoc or infrequent",
      "Assessments focus only on limited systems or external assets",
      "Findings are not prioritized by business impact",
      "Remediation validation is not performed",
      "Assessment results are not incorporated into risk management decisions",
    ],
  },

  "PR.DS-02": {
    title: "Network Communications & Signaling Protection",
    reviewer_guidance:
      "Determine whether the organization protects network communications, signaling traffic, and interconnection pathways against unauthorized access, interception, disruption, or manipulation.",
    evidence_to_review: [
      "Network architecture diagrams",
      "Signaling security controls documentation",
      "Firewall and network access control rules",
      "Encryption and secure transport configuration evidence",
      "Interconnection security agreements",
      "Network monitoring or anomaly detection reports",
    ],
    strong_indicators: [
      "Network communications are encrypted where appropriate",
      "Signaling and interconnection traffic are monitored for abuse or anomalies",
      "Access to network transport and signaling systems is restricted and logged",
      "Network segmentation protects sensitive communications paths",
      "Interconnection points are governed by documented security controls",
    ],
    common_gaps: [
      "Limited visibility into signaling or interconnection traffic",
      "Weak segmentation between network zones",
      "Incomplete encryption coverage for sensitive communications",
      "Insufficient monitoring of telecom-specific signaling abuse",
      "No formal review of interconnection security controls",
    ],
  },
};
